'use client';
import React, { ReactNode, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { resetQuizData } from '@/lib/slices/quiz-slice';

import QuizQuestionBuilderStatic from '@/(portal)/admin/(pages)/courses/components/quiz-setup/QuizQuestionBuilderStatic';
import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import StandardInputSelect from '@/common/components/input/StandardInputSelect';
import StandardInputCheckbox from '@/common/components/input/StandardInputCheckbox';
import StandardInputNumber from '@/common/components/input/StandardInputNumber';
import { ChevronDownIcon, ChevronRightIcon } from '@/assets/icons/chevron';

import Request from '@/services/util/request-service';
import ActiveGroupCacheService from '@/services/storage/active-group-cache-service';
import { AnswerData, QuizBuilderData, QuizQuestionData } from 'quiz-types';
import { CourseData, SubunitId, UnitId } from 'course-types';
import { CancelIcon, SaveIcon } from '@/assets/icons/button';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import LoadingSpinner from '@/common/components/LoadingSpinner';

interface Props {
  visible: boolean;
  onClose: () => void;
  onReviewSuccess: () => void;
  isEditing: boolean;
  baselineQuestions?: Array<QuizQuestionData> | null;
}

type SectionType = 'location' | 'settings' | 'questions' | null;

interface CollapsibleSectionProps {
  title: string;
  description: string;
  isActive: boolean;
  onToggle: () => void;
  children: ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  description,
  isActive,
  onToggle,
  children,
}) => {
  return (
    <div className='group bg-surface_gray_200 rounded-lg border border-border_gray_300 flex flex-col h-fit overflow-hidden'>
      <button
        onClick={onToggle}
        className='w-full flex items-center justify-between p-3 transition-colors flex-shrink-0 min-h-[76px] hover:bg-surface_gray_50/50'
      >
        <div className='flex flex-col gap-1 text-left'>
          <p className='text-base font-medium'>{title}</p>
          <p className='text-sm text-text_gray_900'>{description}</p>
        </div>
        <div className='flex-shrink-0 ml-4'>
          {isActive ? (
            <ChevronDownIcon className='w-5 h-5 text-text_gray_800' />
          ) : (
            <ChevronRightIcon className='w-5 h-5 text-text_gray_800' />
          )}
        </div>
      </button>
      {isActive && (
        <div className='border-t border-border_gray_300 p-3 overflow-y-auto max-h-[40vh] bg-surface_gray_25'>
          {children}
        </div>
      )}
    </div>
  );
};

function QuizReviewModal({
  visible,
  onClose,
  onReviewSuccess,
  isEditing,
  baselineQuestions,
}: Props): React.JSX.Element {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const quizData: QuizBuilderData = useAppSelector(() => store.getState().quiz);
  const questions: Array<QuizQuestionData> = useAppSelector(() => store.getState().quiz.questions);
  const answers: Array<AnswerData> = useAppSelector(() => store.getState().quiz.answers);
  const activeSubunitId: SubunitId | null = useAppSelector(() => store.getState().subunit.id);
  const activeUnitId: UnitId | null = useAppSelector(() => store.getState().unit.id);
  const activityIndex: number = useAppSelector(() => store.getState().activity.index);
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  const activeAllowedAttempts: number | null = useAppSelector(() => store.getState().quiz.attempts);
  const activeDueDate: Date | null = useAppSelector(() => store.getState().quiz.dueDate);
  const activeLockQuiz: boolean = useAppSelector(() => store.getState().quiz.lockQuiz);
  const activeBlockDownstream: boolean = useAppSelector(
    () => store.getState().quiz.blocksDownstream,
  );

  const [selectedSubunit, setSelectedSubunit] = useState<SubunitId>();
  const [selectedUnit, setSelectedUnit] = useState<UnitId>();
  const [attachMode, setAttachMode] = useState<'Unit' | 'Subunit'>('Subunit');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [allowedAttempts, setAllowedAttempts] = useState<number | null | ''>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [lockQuiz, setLockQuiz] = useState<boolean>(false);
  const [blocksDownstreamContent, setBlocksDownstreamContent] = useState<boolean>(false);
  const [minimumGradeThreshold, setMinimumGradeThreshold] = useState<number | null | ''>(null);
  const [requiresMinimumGrade, setRequiresMinimumGrade] = useState<boolean>(false);

  const toDate = (val: unknown): Date | null => {
    if (!val) return null;
    return val instanceof Date ? val : new Date(val as string);
  };

  const [activeSection, setActiveSection] = useState<SectionType>('location');

  useEffect(() => {
    if (activeUnitId && !activeSubunitId) {
      setSelectedSubunit(undefined);
      setSelectedUnit(activeUnitId);
      setAttachMode('Unit');
    } else if (activeSubunitId) {
      setSelectedSubunit(activeSubunitId);
      setSelectedUnit(undefined);
      setAttachMode('Subunit');
    }
    setAllowedAttempts(activeAllowedAttempts);
    setDueDate(toDate(activeDueDate));
    setLockQuiz(activeLockQuiz);
    setBlocksDownstreamContent(activeBlockDownstream);
    setRequiresMinimumGrade(requiresMinimumGrade);
    setMinimumGradeThreshold(minimumGradeThreshold);
  }, [
    activeSubunitId,
    activeUnitId,
    activeAllowedAttempts,
    activeDueDate,
    activeLockQuiz,
    activeBlockDownstream,
  ]);

  const getUnitOptions = () => {
    if (!activeCourseData || !activeCourseData.units) return [];

    return Object.entries(activeCourseData.units)
      .map(([unitId, unitData]) => ({ id: unitId as UnitId, ...unitData }))
      .sort((a, b) => a.index - b.index)
      .map((unit) => ({
        value: unit.id,
        label: unit.name,
      }));
  };

  const getSubunitOptions = () => {
    if (!activeCourseData || !activeCourseData.units) return [];

    return Object.entries(activeCourseData.units)
      .map(([unitId, unitData]) => ({ id: unitId as UnitId, ...unitData }))
      .sort((a, b) => a.index - b.index)
      .flatMap((unit) =>
        Object.entries(unit.subunits)
          .map(([subunitId, subunitData]) => ({
            id: subunitId as SubunitId,
            ...subunitData,
          }))
          .sort((a, b) => a.index - b.index)
          .map((subunit) => ({
            value: subunit.id,
            label: subunit.name,
          })),
      );
  };

  const handleSectionToggle = (section: SectionType) => {
    setActiveSection(section === activeSection ? null : section);
  };

  const renderLocationSelector = () => (
    <div className='flex flex-col gap-3'>
      <StandardInputSelect
        id='attachMode'
        label='Attach To'
        value={attachMode}
        onChange={(e) => {
          setAttachMode(e.target.value as 'Unit' | 'Subunit');
          if (e.target.value === 'Unit') {
            setSelectedSubunit(undefined);
          } else {
            setSelectedUnit(undefined);
          }
        }}
        options={[
          { value: 'Unit', label: 'Unit' },
          { value: 'Subunit', label: 'Subunit' },
        ]}
        isRequired={true}
      />

      {attachMode === 'Unit' ? (
        <StandardInputSelect
          id='selectedUnit'
          label='Select Unit'
          value={selectedUnit || ''}
          onChange={(e) => setSelectedUnit(e.target.value as UnitId)}
          options={[{ value: '', label: 'Select a unit...' }, ...getUnitOptions()]}
          isRequired={true}
        />
      ) : (
        <StandardInputSelect
          id='selectedSubunit'
          label='Select Subunit'
          value={selectedSubunit || ''}
          onChange={(e) => setSelectedSubunit(e.target.value as SubunitId)}
          options={[{ value: '', label: 'Select a subunit...' }, ...getSubunitOptions()]}
          isRequired={true}
        />
      )}
    </div>
  );

  const renderSettings = () => (
    <div className='flex flex-col gap-3'>
      <StandardInputCheckbox
        id='unlimitedAttempts'
        label='Unlimited Attempts'
        checked={allowedAttempts === null}
        onChange={() => {
          if (allowedAttempts === null) {
            setAllowedAttempts(activeAllowedAttempts ?? 1);
          } else {
            setAllowedAttempts(null);
          }
        }}
      />

      {allowedAttempts !== null && (
        <StandardInputNumber
          id='allowedAttempts'
          label='Allowed Attempts'
          value={allowedAttempts === null ? 1 : allowedAttempts}
          onChange={(e) => {
            const value = e.target.value === '' ? '' : Math.max(1, Number(e.target.value));
            setAllowedAttempts(value);
          }}
          min={1}
          isRequired={true}
        />
      )}

      <StandardInputCheckbox
        id='requiresMinimumGrade'
        label='Requires Minimum Grade'
        checked={requiresMinimumGrade}
        onChange={() => {
          const newValue = !requiresMinimumGrade;
          setRequiresMinimumGrade(newValue);
          // Set default value when enabling minimum grade requirement
          if (newValue && minimumGradeThreshold === null) {
            setMinimumGradeThreshold(80);
          }
        }}
      />

      {requiresMinimumGrade && (
        <StandardInputNumber
          id='minimumGradeThreshold'
          label='Minimum Grade'
          value={minimumGradeThreshold === null ? 80 : minimumGradeThreshold}
          onChange={(e) => {
            const value = e.target.value === '' ? '' : Math.max(1, Number(e.target.value));
            setMinimumGradeThreshold(value);
          }}
          min={1}
          isRequired={true}
        />
      )}

      <StandardInputCheckbox
        id='blocksDownstreamContent'
        label='Block Downstream Content'
        checked={blocksDownstreamContent}
        onChange={() => setBlocksDownstreamContent(!blocksDownstreamContent)}
      />

      <StandardInputCheckbox
        id='hasDueDate'
        label='Set Due Date'
        checked={dueDate !== null}
        onChange={() => {
          if (dueDate === null) {
            if (activeDueDate !== null) {
              setDueDate(toDate(activeDueDate));
            } else {
              const today = new Date();
              today.setHours(23, 59, 0, 0);
              setDueDate(today);
            }
          } else {
            setDueDate(null);
          }
        }}
      />

      {dueDate !== null && (
        <StandardInputCheckbox
          id='lockQuiz'
          label='Lock After Due Date'
          checked={lockQuiz}
          onChange={() => setLockQuiz(!lockQuiz)}
        />
      )}

      {dueDate !== null && (
        <div className='flex flex-col gap-1'>
          <label htmlFor='dueDate' className='text-sm font-medium'>
            Due Date
          </label>
          <input
            type='datetime-local'
            id='dueDate'
            className='rounded-lg border border-border_gray_200 bg-surface_gray_50 px-3 py-2'
            value={formatDate(dueDate)}
            onChange={(e) => {
              const date = new Date(e.target.value);
              // If user only picked a date (hours==0, minutes==0) set default time 23:59
              if (date.getHours() === 0 && date.getMinutes() === 0) {
                date.setHours(23, 59, 0, 0);
              }
              setDueDate(date);
            }}
            required
          />
        </div>
      )}
    </div>
  );

  const renderQuestions = () => (
    <div className='flex flex-col gap-2 overflow-y-auto pr-1'>
      {questions.map((question) => (
        <div
          key={question.index}
          className='bg-surface_gray_50 rounded-lg p-3 border border-border_gray_300 flex-shrink-0'
        >
          <QuizQuestionBuilderStatic
            index={question.index}
            question={question.question}
            type={question.type}
            options={question.options}
          />
        </div>
      ))}
    </div>
  );

  const formatDate = (dateInput: Date | null): string => {
    if (!dateInput) return '';
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput as unknown as string);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await handleSubmitLogic();
      dispatch(resetQuizData());
      onReviewSuccess();
      toast.success(`Successfully ${isEditing ? 'updated' : 'created'} quiz`);
    } catch (error) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} quiz`);
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  // Build a stable signature for question identity (content-based; ignores index)
  const qSig = (q: QuizQuestionData) =>
    JSON.stringify({
      t: q.type,
      q: (q.question || '').trim(),
      o: (q.options || []).map((opt) => (opt?.value || '').trim()),
    });

  // Create transient editor flags for submit only
  const withEditorMeta = (
    current: Array<QuizQuestionData>,
    baseline: Array<QuizQuestionData> | null | undefined,
  ): Array<any> => {
    if (!isEditing || !baseline) {
      // New quiz: mark all as new
      return current.map((q) => ({ ...q, editor_meta: { isNew: true } }));
    }

    // Multiset of baseline signatures to account for moves/duplicates
    const baseCounts = new Map<string, number>();
    baseline.forEach((bq) => {
      const s = qSig(bq);
      baseCounts.set(s, (baseCounts.get(s) || 0) + 1);
    });

    return current.map((q, idx) => {
      const s = qSig(q);
      const cnt = baseCounts.get(s) || 0;
      if (cnt > 0) {
        baseCounts.set(s, cnt - 1);
        return { ...q }; // unchanged or moved, no flags
      }
      // content not present in baseline => classify edited vs new using index heuristic
      const baseLen = baseline.length;
      if (idx < baseLen) {
        // If there was a baseline question in this slot and signatures differ, treat as edited
        return { ...q, editor_meta: { isEdited: true } };
      }
      return { ...q, editor_meta: { isNew: true } };
    });
  };

  const handleSubmitLogic = async () => {
    const groupId = ActiveGroupCacheService.getCurrentGroupCookie();
    const body: any = {
      name: quizData.name,
      quiz_data: withEditorMeta(quizData.questions, baselineQuestions),
      answer_key: answers,
      due_date: dueDate ? dueDate.toISOString() : null,
      attempts: allowedAttempts,
      groupId: groupId,
      lock_quiz: lockQuiz,
      requires_minimum_grade: requiresMinimumGrade,
      minimum_grade_threshold: minimumGradeThreshold || null,
      blocks_downstream_content: blocksDownstreamContent,
    };

    if (selectedSubunit) {
      body.subunitId = selectedSubunit ?? activeSubunitId;
    } else {
      body.unitId = selectedUnit ?? activeUnitId;
    }

    // Set index value
    if (isEditing && activityIndex !== -1 && activeSubunitId === selectedSubunit) {
      body.index = activityIndex;
    } else {
      if (selectedSubunit) {
        // For subunit-level activities
        const sub = Object.values(activeCourseData.units)
          .map((unit) => unit.subunits[selectedSubunit as SubunitId])
          .filter((s) => s !== undefined);
        body.index = sub.length > 0 ? sub[0].activities.length : 0;
      } else {
        // For unit-level activities
        const unitId = selectedUnit ?? activeUnitId;
        if (unitId) {
          const unit = activeCourseData.units[unitId];
          body.index = unit?.activities ? unit.activities.length : 0;
        } else {
          body.index = 0;
        }
      }
    }

    // Call endpoint
    let res;
    if (isEditing) {
      res = await (await Request.post('quiz/update', { id: quizData.id, ...body }))?.json();
    } else {
      res = await (await Request.post('quiz', { ...body }))?.json();
    }

    if (!res || res.error || !res.content) {
      throw new Error('Request failed.');
    }

    const createdQuiz = res.content;
    // pull the latest course data to recalculate activity prerequisites
    await UserCourseStorageService.setCourseData(createdQuiz.id);
  };

  const handleClose = () => {
    onClose();
  };

  if (!visible) return <></>;

  return (
    <ModalComponent
      isOpen={visible}
      onRequestClose={handleClose}
      contentLabel={isEditing ? 'Edit Quiz' : 'Review & Save Quiz'}
      contentSubtitle={
        isEditing ? 'Update quiz settings and location' : 'Configure and save your quiz'
      }
      className='max-w-[800px] w-full'
      closeOnClickOff={!isLoading}
    >
      <div className='relative w-full h-[76vh] flex flex-col'>
        <div className='flex-1 flex flex-col gap-2 overflow-hidden pr-2'>
          <div className='flex flex-col gap-2 w-full h-full'>
            <CollapsibleSection
              title='Select where to add this quiz'
              description='Choose whether to attach this quiz to a unit or subunit'
              isActive={activeSection === 'location'}
              onToggle={() => handleSectionToggle('location')}
            >
              {renderLocationSelector()}
            </CollapsibleSection>

            <CollapsibleSection
              title='Quiz Settings'
              description='Configure attempts and due date for this quiz'
              isActive={activeSection === 'settings'}
              onToggle={() => handleSectionToggle('settings')}
            >
              {renderSettings()}
            </CollapsibleSection>

            <CollapsibleSection
              title='Quiz Questions'
              description={`Review ${questions.length} question${questions.length !== 1 ? 's' : ''}`}
              isActive={activeSection === 'questions'}
              onToggle={() => handleSectionToggle('questions')}
            >
              {renderQuestions()}
            </CollapsibleSection>
          </div>
        </div>

        <div className='flex flex-row gap-8 py-2 border-t border-border_gray_300 mt-4 pt-4'>
          <Button
            onClick={handleSubmit}
            disabled={
              isLoading ||
              allowedAttempts === '' ||
              (!selectedUnit && !selectedSubunit) ||
              questions.length === 0
            }
          >
            <SaveIcon className='w-[20px] h-[20px] mr-2' />
            {isEditing ? 'Update Quiz' : 'Create Quiz'}
          </Button>
          <Button outline onClick={handleClose} disabled={isLoading}>
            <CancelIcon className='w-[20px] h-[20px] mr-2' /> Cancel
          </Button>
        </div>

        {isLoading && (
          <div className='absolute top-0 left-0 z-10 w-full h-full flex items-center justify-center'>
            <div className='absolute inset-0 bg-surface_gray_50 opacity-70' />
            <div className='flex flex-col items-center justify-center gap-2 text-sm z-20'>
              <LoadingSpinner />
              <div>{isEditing ? 'Updating' : 'Creating'} quiz...</div>
            </div>
          </div>
        )}
      </div>
    </ModalComponent>
  );
}

export default QuizReviewModal;
